import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Navbar } from '../components/layout/Navbar';
import { toast } from 'sonner';
import { Mail, Lock, User, Loader2, ShieldAlert } from 'lucide-react';
import { WyrdMakerLogo } from '../components/ui/WyrdMakerLogo';

export default function RegisterPage() {
  const { register, language } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invitationToken = searchParams.get('token');

  const [loading, setLoading] = useState(false);
  const [betaMode, setBetaMode] = useState(false);
  const [tokenChecked, setTokenChecked] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    const bootstrap = async () => {
      try {
        const statusRes = await axios.get('/api/beta/status');
        const isBeta = !!statusRes.data?.beta_mode_enabled;
        setBetaMode(isBeta);

        if (invitationToken) {
          try {
            const res = await axios.get(`/api/beta/validate-token/${invitationToken}`);
            setFormData((prev) => ({ ...prev, email: res.data.email }));
            setTokenValid(true);
          } catch (err) {
            setTokenValid(false);
            setTokenError(
              err.response?.status === 410
                ? (language === 'fr' ? 'Ce lien d\'invitation a expiré.' : 'This invitation link has expired.')
                : (language === 'fr' ? 'Lien d\'invitation invalide ou déjà utilisé.' : 'Invalid or already used invitation link.')
            );
          }
        }
      } catch (_) {
        // fail silently — backend status unavailable
      } finally {
        setTokenChecked(true);
      }
    };
    bootstrap();
  }, [invitationToken, language]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error(language === 'fr' ? 'Les mots de passe ne correspondent pas' : 'Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error(language === 'fr' ? 'Le mot de passe doit contenir au moins 6 caractères' : 'Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await register(formData.email, formData.password, formData.username, invitationToken);
      toast.success(language === 'fr' ? 'Inscription réussie !' : 'Registration successful!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('register_error', language));
    } finally {
      setLoading(false);
    }
  };

  // Beta mode ON + no valid token → show a "closed beta" notice
  const registrationBlocked = tokenChecked && betaMode && (!invitationToken || !tokenValid);

  return (
    <div className="min-h-screen bg-background" data-testid="register-page">
      <Navbar />

      <div className="flex items-center justify-center px-4 py-20">
        <Card className="w-full max-w-md glass-card animate-scale-in">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-fit">
              <WyrdMakerLogo size="lg" showText={false} className="text-primary" />
            </div>
            <CardTitle className="font-cinzel text-2xl">{t('register_title', language)}</CardTitle>
            <CardDescription>{t('app_subtitle', language)}</CardDescription>
          </CardHeader>

          {registrationBlocked ? (
            <CardContent className="space-y-4 text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                <ShieldAlert className="h-6 w-6" />
              </div>
              <h3 className="font-cinzel text-lg font-bold">
                {language === 'fr' ? 'Beta fermée' : 'Closed beta'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {tokenError || (language === 'fr'
                  ? "L'inscription est actuellement réservée aux personnes invitées. Rendez-vous sur la page d'accueil pour demander l'accès."
                  : 'Registration is currently invite-only. Head back to the home page to request access.')}
              </p>
              <Button className="w-full fantasy-button" onClick={() => navigate('/')} data-testid="back-home">
                {language === 'fr' ? "Demander l'accès" : 'Request access'}
              </Button>
              <p className="text-sm text-muted-foreground">
                {t('has_account', language)}{' '}
                <Link to="/login" className="text-primary hover:underline">{t('login', language)}</Link>
              </p>
            </CardContent>
          ) : (
            <form onSubmit={handleSubmit}>
              <CardContent className="space-y-4">
                {invitationToken && tokenValid && (
                  <div className="rounded-md bg-primary/10 border border-primary/30 p-3 text-xs text-center text-primary">
                    {language === 'fr'
                      ? '🎉 Invitation beta valide — finalisez votre inscription.'
                      : '🎉 Valid beta invitation — finish your registration.'}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="username">{t('username', language)}</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="username"
                      type="text"
                      placeholder={language === 'fr' ? 'Votre pseudo' : 'Your username'}
                      className="pl-10"
                      value={formData.username}
                      onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                      required
                      data-testid="register-username"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">{t('email', language)}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="dm@example.com"
                      className="pl-10"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      required
                      readOnly={tokenValid}
                      data-testid="register-email"
                    />
                  </div>
                  {tokenValid && (
                    <p className="text-xs text-muted-foreground">
                      {language === 'fr' ? 'Email lié à votre invitation — non modifiable.' : 'Email tied to your invitation — not editable.'}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">{t('password', language)}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      className="pl-10"
                      value={formData.password}
                      onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                      required
                      data-testid="register-password"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">
                    {language === 'fr' ? 'Confirmer le mot de passe' : 'Confirm password'}
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="••••••••"
                      className="pl-10"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      required
                      data-testid="register-confirm-password"
                    />
                  </div>
                </div>
              </CardContent>

              <CardFooter className="flex flex-col gap-4">
                <Button
                  type="submit"
                  className="w-full fantasy-button"
                  disabled={loading}
                  data-testid="register-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('loading', language)}
                    </>
                  ) : (
                    t('register', language)
                  )}
                </Button>

                <p className="text-sm text-muted-foreground text-center">
                  {t('has_account', language)}{' '}
                  <Link to="/login" className="text-primary hover:underline" data-testid="login-link">
                    {t('login', language)}
                  </Link>
                </p>
              </CardFooter>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
}
